import { Font } from '@utmb/design-system/text/font';
import { ReactElement, useState } from 'react';
import { BlockDashboardWrapper } from '@utmb/features/my-utmb/BlockDashboardWrapper';
import styles from '@utmb/features/my-dashboard/my-dashboard.module.scss';
import AchievementsList from '../my-achievements/AchievementsList';
import RegistrationsList from '../my-registrations/RegistrationsList';
import { useTranslation } from 'next-i18next';
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { BlockLatest } from '../my-utmb/BlockLatest';
import { LoyaltyPreview } from '../my-utmb/loyalty-preview/LoyaltyPreview';
import { Placeholder } from '../my-utmb/placeholder/placeholder';
import { MyUtmbLabel } from '../my-utmb/MyUtmbLabel';
import { UtmbPlusPlaceholder } from '../my-utmb/utmb-plus-placeholder/UtmbPlusPlaceholder';
import { MyWishlistSwipeCards } from '../my-wishlist-swipe-cards/MyWishlistSwipeCards';
import { LoyaltyHowToGainPointsModal } from '../my-utmb/loyalty-how-to-gain-points/loyalty-how-to-gain-points-modal/LoyaltyHowToGainPointsModal';

export const MyDashboardMember = (): ReactElement => {
    const { t } = useTranslation('myutmb');

    const { dashboardMember } = useDashboard();
    const [isHowToGainPointsModalOpen, setIsHowToGainPointsModalOpen] = useState<boolean>(false);
    const haveNextRaces = dashboardMember?.worldSeriesRegistrations && dashboardMember.worldSeriesRegistrations.length > 0;
    const isCurrentLevel = typeof dashboardMember?.loyalty?.currentLevel === 'number' && dashboardMember.loyalty.levels[0].minPoints !== -1;

    return (
        <div className={styles.container}>
            <BlockDashboardWrapper
                variant={'dark'}
                className={styles.nextRacesBlock + ' ' + (haveNextRaces ? '' : styles.emptyNextRacesBlock)}
                buttonAction={(dashboardMember?.worldSeriesRegistrations?.length ?? 0) > 0 ? 'my-registrations' : undefined}
                buttonLabel={(dashboardMember?.worldSeriesRegistrations?.length ?? 0) > 0 ? t('myUtmb.registrations.more') : undefined}
                label={
                    <>
                        <Font.FuturaMedium as="span">{t('myUtmb.myDashboard.races1')}</Font.FuturaMedium>
                        &nbsp;
                        <Font.FuturaBold as="span">{t('myUtmb.myDashboard.races2')}</Font.FuturaBold>
                    </>
                }
            >
                <RegistrationsList limit={2} />
            </BlockDashboardWrapper>

            {haveNextRaces ? (
                <BlockDashboardWrapper variant={'dark'} className={styles.nextRacesPlaceholderBlock}>
                    <Placeholder
                        title={t('myUtmb.registration.banner.title')}
                        description={t('myUtmb.registration.banner.description')}
                        cta={{ label: t('myUtmb.registration.banner.link'), action: '/utmb-world-series-events' }}
                        image="/worldseries/MYUTMB+/placeholder-2.jpg"
                        variant="small"
                    />
                </BlockDashboardWrapper>
            ) : null}

            <BlockDashboardWrapper
                variant="dark"
                className={styles.achievementsBlock}
                label={
                    <>
                        <Font.FuturaMedium as="span">{t('myUtmb.myDashboard.achievements1')}</Font.FuturaMedium>
                        &nbsp;
                        <Font.FuturaBold as="span">{t('myUtmb.myDashboard.achievements2')}</Font.FuturaBold>
                    </>
                }
                buttonLabel={t('myUtmb.achievement.more')}
                buttonAction={'my-achievements'}
            >
                <AchievementsList limit={3} />
            </BlockDashboardWrapper>

            <BlockDashboardWrapper
                variant="dark"
                className={styles.loyaltyBlock}
                label={<MyUtmbLabel forcePlus />}
                linkLabel={t('myUtmb.howToGainPoints.link')}
                linkUrl={() => setIsHowToGainPointsModalOpen(true)}
                linkOrSubtitleOnRight
            >
                {isCurrentLevel ? <LoyaltyPreview href="my-rewards" dashboardMember={dashboardMember} /> : <UtmbPlusPlaceholder />}
            </BlockDashboardWrapper>

            {dashboardMember?.followed?.races?.length ? (
                <BlockDashboardWrapper
                    variant="dark"
                    className={styles.followedBlock}
                    label={
                        <>
                            <Font.FuturaMedium as="span">{t('myUtmb.myDashboard.whislist1')}</Font.FuturaMedium>
                            &nbsp;
                            <Font.FuturaBold as="span">{t('myUtmb.myDashboard.whislist2')}</Font.FuturaBold>
                        </>
                    }
                >
                    <MyWishlistSwipeCards races={dashboardMember.followed.races} />
                </BlockDashboardWrapper>
            ) : null}

            <BlockDashboardWrapper
                variant="dark"
                className={styles.roadToUtmbPlaceholderBlock}
                label={
                    <>
                        <Font.FuturaMedium as="span">{t('myUtmb.myDashboard.roadToUtmb1')}</Font.FuturaMedium>
                        &nbsp;
                        <Font.FuturaBold as="span">{t('myUtmb.myDashboard.roadToUtmb2')}</Font.FuturaBold>
                    </>
                }
            >
                <Placeholder
                    logo="/montblanc/Logo_UTMB_Mont_Blanc_HOKA_2024_white_6416212da1.png"
                    cta={{ label: t('myUtmb.myDashboard.finalsCheckBtn'), action: '/my-utmb/my-road-to-utmb' }}
                    image="/montblanc/Races/CCC/hoka_utmb_mont_blanc_2024_ccc_utmb24_ccc_fo_00_0100_a40e1a0529.jpg"
                    variant="big"
                />
            </BlockDashboardWrapper>

            <div className={styles.latestBlock}>
                <BlockLatest />
            </div>

            {dashboardMember?.loyalty ? (
                <LoyaltyHowToGainPointsModal
                    currentLevel={dashboardMember.loyalty.currentLevel}
                    loyaltyRules={dashboardMember.loyaltyRules}
                    isOpen={isHowToGainPointsModalOpen}
                    close={() => setIsHowToGainPointsModalOpen(false)}
                />
            ) : null}
        </div>
    );
};
